
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  GraduationCap, 
  BookOpen, 
  Video, 
  Award, 
  Clock,
  CheckCircle,
  Play,
  Users
} from 'lucide-react';

const HQTraining = () => {
  const trainingModules = [
    {
      id: 1,
      title: "JUV Fundamentals",
      description: "Core principles, brand standards, and company culture",
      duration: "2 hours",
      completion: 95,
      enrolled: 28,
      type: "required"
    },
    {
      id: 2,
      title: "Botox Injection Techniques",
      description: "Advanced injection methods and safety protocols",
      duration: "4 hours", 
      completion: 78,
      enrolled: 12,
      type: "clinical"
    },
    {
      id: 3,
      title: "Customer Service Excellence",
      description: "Patient communication and experience optimization",
      duration: "1.5 hours",
      completion: 88,
      enrolled: 24,
      type: "required"
    },
    {
      id: 4,
      title: "Dermal Filler Mastery",
      description: "Product knowledge and advanced techniques",
      duration: "6 hours",
      completion: 65,
      enrolled: 8,
      type: "clinical"
    }
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'required': return 'bg-red-500/20 text-red-200 border-red-500/30';
      case 'clinical': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'optional': return 'bg-green-500/20 text-green-200 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Training System
              </h1>
              <p className="mt-1 text-amber-200">
                LMS system for staff onboarding powered by Alma
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <BookOpen className="w-4 h-4 mr-2" />
              Create Course
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Training Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <GraduationCap className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Active Courses</p>
                    <p className="text-2xl font-bold text-white">12</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <Users className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Enrolled Staff</p>
                    <p className="text-2xl font-bold text-white">72</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <Award className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Certifications</p>
                    <p className="text-2xl font-bold text-white">156</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-purple-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-purple-500/20">
                    <CheckCircle className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-200">Completion Rate</p>
                    <p className="text-2xl font-bold text-white">87%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Training Modules */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Training Modules</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {trainingModules.map((module) => (
                  <div key={module.id} className="p-6 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold">{module.title}</h3>
                          <Badge className={getTypeColor(module.type)}>
                            {module.type}
                          </Badge>
                        </div>
                        <p className="text-slate-300 text-sm mb-3">{module.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-400">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{module.duration}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{module.enrolled} enrolled</span>
                          </div>
                        </div>
                      </div>
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                        <Play className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">Completion Rate</span>
                        <span className="text-white">{module.completion}%</span>
                      </div>
                      <Progress value={module.completion} className="h-2" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white">Recent Completions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: "Emily Rodriguez", course: "Botox Fundamentals", time: "2 hours ago" },
                    { name: "Michael Chen", course: "Customer Service", time: "4 hours ago" },
                    { name: "Sarah Kim", course: "JUV Fundamentals", time: "1 day ago" },
                    { name: "David Park", course: "Safety Protocols", time: "1 day ago" }
                  ].map((activity, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{activity.name}</p>
                        <p className="text-slate-300 text-sm">Completed {activity.course}</p>
                      </div>
                      <span className="text-slate-400 text-sm">{activity.time}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white">Training Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Average Course Completion Time</p>
                    <p className="text-2xl font-bold text-white">3.2 days</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Most Popular Course</p>
                    <p className="text-white font-semibold">JUV Fundamentals</p>
                    <p className="text-slate-400 text-sm">28 enrollments this month</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Certification Pending</p>
                    <p className="text-amber-400 font-semibold">5 staff members</p>
                    <p className="text-slate-400 text-sm">Need to complete final assessments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQTraining;
